/**
 * AI Assistant V2 - List-Detail Layout Test
 * Clean structure with proper scroll containment
 */

import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../../auth';
import ReactMarkdown from 'react-markdown';
import './AIAgentV2.css';

const SETTINGS_SECTIONS = [
  { id: 'agent', label: 'Agent', icon: '🤖' },
  { id: 'voice', label: 'Voice', icon: '🎤' },
  { id: 'access', label: 'Access', icon: '🔐' },
  { id: 'email', label: 'Email', icon: '📧' },
  { id: 'keys', label: 'API Keys', icon: '🔑' },
  { id: 'advanced', label: 'Advanced', icon: '⚙️' },
  { id: 'security', label: 'Security', icon: '🛡️' },
  { id: 'security', label: 'Security', icon: '🛡️' },
];

const AIAgentV2 = ({ user }) => {
  const [activeTab, setActiveTab] = useState('chat');
  const [settingsSection, setSettingsSection] = useState('agent');
  const [config, setConfig] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [accessView, setAccessView] = useState('owner');
  const [localAccess, setLocalAccess] = useState({});
  const [accessSaved, setAccessSaved] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const accessData = (config && config.access) || {};
  const guestData = (config && config.guestAccess) || {};
  const currentAccess = accessView === 'guest' ? guestData : accessData;
  useEffect(function() { setLocalAccess(JSON.parse(JSON.stringify(currentAccess))); }, [config, accessView]);

  const loadData = async () => {
    try {
      const [statusRes, configRes, historyRes] = await Promise.all([
        authFetch('/api/aiassistant/status'),
        authFetch('/api/aiassistant/config'),
        authFetch('/api/aiassistant/history?limit=50'),
      ]);
      if (statusRes.ok) setStatus(await statusRes.json());
      if (configRes.ok) setConfig(await configRes.json());
      if (historyRes.ok) {
        const data = await historyRes.json();
        const allMsgs = (data.sessions || []).flatMap(s => s.messages || []);
        setMessages(allMsgs.slice(-50));
      }
    } catch (e) {
      console.error('Load error:', e);
    }
    setLoading(false);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || sending) return;
    const userMsg = { role: 'user', content: chatInput, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setSending(true);
    try {
      const res = await authFetch('/api/aiassistant/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: chatInput, tier: 'owner' }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply, timestamp: Date.now() }]);
      }
    } catch (e) {
      console.error('Send error:', e);
    }
    setSending(false);
  };

  if (loading) {
    return <div className="v2-loading">Loading...</div>;
  }

  return (
    <div className="v2-shell">
      {/* Top tabs: Chat / Settings / Number */}
      <div className="v2-tabs">
        {['chat', 'settings', 'number'].map(tab => (
          <button
            key={tab}
            className={`v2-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="v2-content">
        {activeTab === 'chat' && (
          <div className="v2-chat">
            <div className="v2-chat-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`v2-msg ${msg.role}`}>
                  <div className="v2-msg-content">
                    {msg.role === 'assistant' ? <ReactMarkdown>{msg.content}</ReactMarkdown> : msg.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="v2-msg assistant">
                  <div className="v2-msg-content v2-thinking">...</div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <form className="v2-chat-input" onSubmit={sendMessage}>
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                placeholder="Type a message..."
                disabled={sending}
              />
              <button type="submit" disabled={sending || !chatInput.trim()}>Send</button>
            </form>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="v2-settings">
            {/* List */}
            <div className="v2-settings-list">
              {SETTINGS_SECTIONS.map(s => (
                <button
                  key={s.id}
                  className={`v2-settings-item ${settingsSection === s.id ? 'active' : ''}`}
                  onClick={() => setSettingsSection(s.id)}
                >
                  <span className="v2-settings-icon">{s.icon}</span>
                  <span className="v2-settings-label">{s.label}</span>
                </button>
              ))}
            </div>

            {/* Detail */}
            <div className="v2-settings-detail">
              <SettingsDetail section={settingsSection} config={config} accessView={accessView} setAccessView={setAccessView} localAccess={localAccess} setLocalAccess={setLocalAccess} accessSaved={accessSaved} setAccessSaved={setAccessSaved} />
            </div>
          </div>
        )}

        {activeTab === 'number' && (
          <div className="v2-panel">
            <h3>Phone Number</h3>
            <p>{config?.phoneNumber || 'No number assigned'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const SettingsDetail = ({ section, config, accessView, setAccessView, localAccess, setLocalAccess, accessSaved, setAccessSaved }) => {
  const c = config?.config || {};

  const renderContent = () => {
    switch (section) {
      case 'agent':
        return (
          <>
            <h3>Agent Settings</h3>
            <div className="v2-form-group">
              <label>Agent Name</label>
              <input type="text" defaultValue={c.agentName || 'AI Assistant'} />
            </div>
            <div className="v2-form-group">
              <label>Greeting</label>
              <input type="text" defaultValue={c.greeting || ''} placeholder="Hi, how can I help?" />
            </div>
            <div className="v2-form-group">
              <label>Personality & Instructions</label>
              <textarea rows={6} defaultValue={c.systemPrompt || ''} placeholder="You are a helpful assistant..." />
            </div>
            <div className="v2-form-group">
              <label>AI Model</label>
              <select defaultValue={c.llmModel || 'google/gemini-3-flash-preview'}>
                <option value="google/gemini-3-flash-preview">Gemini 3 Flash</option>
                <option value="anthropic/claude-sonnet-4">Claude Sonnet 4</option>
                <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
              </select>
            </div>
            <button className="v2-btn-save">Save Changes</button>
          </>
        );

      case 'voice':
        return (
          <>
            <h3>Voice Settings</h3>
            <div className="v2-form-group">
              <label>TTS Engine</label>
              <select defaultValue={c.ttsEngine || 'edge'}>
                <option value="edge">Edge TTS (Free)</option>
                <option value="elevenlabs">ElevenLabs</option>
              </select>
            </div>
            <div className="v2-form-group">
              <label>Voice</label>
              <select defaultValue={c.voiceId || 'sarah'}>
                <option value="sarah">Sarah (Warm)</option>
                <option value="james">James (Professional)</option>
                <option value="sofia">Sofia (Friendly)</option>
              </select>
            </div>
            <button className="v2-btn-save">Save Changes</button>
          </>
        );

      case 'access':
        var accessResources = [
          { id: 'contacts', label: 'Contacts', desc: 'Address book management' },
          { id: 'calendar', label: 'Calendar', desc: 'Private events — full details, titles, locations' },
          { id: 'calendar_availability', label: 'Calendar Availability', desc: 'Public free/busy slots + guest booking. No private details.' },
          { id: 'notes', label: 'Notes', desc: 'Personal and shared notes' },
          { id: 'tasks', label: 'Tasks', desc: 'To-do items and reminders' },
          { id: 'sms', label: 'SMS', desc: 'Text message history and sending' },
          { id: 'dictation', label: 'Dictation', desc: 'Call handling, transcripts, transfer' },
          { id: 'linktime', label: 'LinkTime', desc: 'Room meeting links and invites' },
          { id: 'web', label: 'Web', desc: 'Web search and AI research (news, social, trends)' },
          { id: 'travel', label: 'Travel', desc: 'Airport status, flight delays, TSA' },
          { id: 'email', label: 'Email', desc: 'IMAP email read, search, and reply' },
          { id: 'system', label: 'System', desc: 'Model switching and system operations' },
          { id: 'conversations', label: 'Conversations', desc: 'Chat history search across channels' },
        ];
        var togglePerm = function(resource, perm) {
          setLocalAccess(function(prev) {
            var next = JSON.parse(JSON.stringify(prev));
            if (!next[resource]) next[resource] = {};
            var has = (next[resource][perm] === true);
            next[resource][perm] = !has;
            if (!next[resource].read && !next[resource].write && !next[resource].delete) delete next[resource];
            return next;
          });
          setAccessSaved(false);
        };
        var saveAccess = async function() {
          var updates = {};
          if (accessView === 'guest') updates.guestAccess = localAccess;
          else updates.access = localAccess;
          try {
            var res = await authFetch('/api/aiassistant/config', { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify(updates) });
            if (res.ok) setAccessSaved(true);
          } catch(e) {}
        };
        return (
          <>
            <h3>Access Control</h3>
            <p className="v2-desc">Control what your assistant can access across all tools and resources.</p>
            <div className="v2-access-tabs">
              <button className={'v2-access-tab' + (accessView === 'owner' ? ' active' : '')} onClick={function(){setAccessView('owner')}}>Owner (You)</button>
              <button className={'v2-access-tab' + (accessView === 'guest' ? ' active' : '')} onClick={function(){setAccessView('guest')}}>Guest (Callers)</button>
            </div>
            <div className="v2-access-grid">
              <div className="v2-access-header">
                <span className="v2-access-res-label">Resource</span>
                <span className="v2-access-perm">Read</span>
                <span className="v2-access-perm">Write</span>
                <span className="v2-access-perm">Delete</span>
              </div>
              {accessResources.map(function(r) {
                var perms = localAccess[r.id] || {};
                return (
                  <label key={r.id} className="v2-access-row" title={r.desc}>
                    <span className="v2-access-res-label">{r.label}</span>
                    <input type="checkbox" checked={perms.read === true} onChange={function(){togglePerm(r.id,'read')}} />
                    <input type="checkbox" checked={perms.write === true} onChange={function(){togglePerm(r.id,'write')}} />
                    <input type="checkbox" checked={perms.delete === true} onChange={function(){togglePerm(r.id,'delete')}} />
                  </label>
                );
              })}
            </div>
            {accessSaved && <p className="v2-saved-msg">Access control updated.</p>}
            <button className="v2-btn-save" onClick={saveAccess}>Save Changes</button>
          </>
        );

      case 'email':
        return (
          <>
            <h3>Email Settings</h3>
            <p className="v2-desc">Configure email notifications and forwarding.</p>
            <div className="v2-form-group">
              <label>Notification Email</label>
              <input type="email" placeholder="you@example.com" />
            </div>
            <button className="v2-btn-save">Save Changes</button>
          </>
        );

      case 'keys':
        return (
          <>
            <h3>API Keys</h3>
            <p className="v2-desc">Add your own API keys for extended functionality.</p>
            <div className="v2-form-group">
              <label>ElevenLabs API Key</label>
              <input type="password" placeholder="sk_..." />
            </div>
            <div className="v2-form-group">
              <label>OpenAI API Key</label>
              <input type="password" placeholder="sk-..." />
            </div>
            <button className="v2-btn-save">Save Changes</button>
          </>
        );

      case 'advanced':
        return (
          <>
            <h3>Advanced Settings</h3>
            <div className="v2-form-group">
              <label>Temperature ({c.temperature || 0.7})</label>
              <input type="range" min="0" max="1" step="0.1" defaultValue={c.temperature || 0.7} />
            </div>
            <div className="v2-form-group">
              <label>Max Tokens</label>
              <input type="number" defaultValue={c.maxTokensWeb || 500} />
            </div>
            <button className="v2-btn-save">Save Changes</button>
          </>
        );

      case 'security':
        return (
          <>
            <h3>Security</h3>
            <div className="v2-form-group">
              <label>Owner Numbers (one per line)</label>
              <textarea rows={3} placeholder="+16507321013" />
            </div>
            <div className="v2-form-group">
              <label>Transfer Allowlist</label>
              <textarea rows={3} placeholder="+16507321013 Nick" />
            </div>
            <button className="v2-btn-save">Save Changes</button>
          </>
        );

      default:
        return <p>Select a section</p>;
    }
  };

  return <div className="v2-detail-content">{renderContent()}</div>;
};

export default AIAgentV2;
